import React, { CSSProperties, SyntheticEvent, useCallback, useMemo, useState } from "react";

import { Button, ButtonProps, Icon, Popup } from "semantic-ui-react";

import { toast } from "react-toastify";

import SubBillLockNotiMessage, {
  BillLockNotiMessageItem,
} from "react-lib/apps/HISV3/BIL/SubBillLockNotiMessage";

// Types
type NotificationMessageProps = {
  onEvent: (e: any) => void;
  setProp: (key: string, value: any, callback?: () => void) => any;
  billingLockNotificationList?: Record<string, any>[];
  billLockNotiMessageList?: Record<string, any>[];
  rejectOrderNotificationList?: Record<string, any>[];
  showIconNoti?: boolean;
  tabPatientRef?: { changeTabKey: (tab: string, callback: () => void) => void };
  django?: any;
  onClickBell?: () => void;
};

type Styles = Record<"box" | "button" | "buttonActive" | "icon" | "popup", CSSProperties>;

// Constants
const styles: Styles = {
  box: {
    backgroundColor: "unset",
    borderRadius: "5px",
    boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.1)",
    margin: "0.1rem 0.25rem 1rem",
  },
  button: { width: "11rem" },
  buttonActive: { backgroundColor: "var(--primary-theme-color)", color: "white", width: "11rem" },
  icon: {
    color: "white",
    cursor: "pointer",
    fontSize: "1.4em",
  },
  popup: { minWidth: "43rem" },
};

const TITLES = {
  COMPLETED: "ดำเนินการแล้ว",
  PENDING: "ยังไม่ได้ดำเนินการ",
};

const POPUP_OFFSET: [number, number] = [8, 0];

const NotificationMessage = (props: NotificationMessageProps) => {
  const [tab, setTab] = useState<"COMPLETED" | "PENDING">("PENDING");
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleOpenPendingInvoice = useCallback(
    (data: BillLockNotiMessageItem) => () => {
      props.tabPatientRef?.changeTabKey("Encounter", () => {
        props.setProp("initialLayout", 1);

        props.onEvent({
          message: "BillingQueueAction",
          params: {
            action: "select_encounter",
            encounterId: data.encounter,
          },
        });

        handleClose();

        // ปิด toast ด้วยเมื่อมี toast เปิดค้างอยู่
        const messages = props.billLockNotiMessageList ?? [];
        const foundNoti = messages.find((item) => item.id === data.encounter);

        if (!foundNoti) {
          return;
        }

        toast.dismiss(foundNoti.time);

        const filttered = messages.filter((item) => item.id !== foundNoti.id);

        props.setProp("billLockNotiMessageList", filttered);
      });
    },
    [props.billLockNotiMessageList, props.tabPatientRef]
  );

  const handleChangeTab = useCallback((e: SyntheticEvent, data: ButtonProps) => {
    setTab(data.name);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(!props.showIconNoti);
  }, [props.showIconNoti]);

  const groupData = useMemo(() => {
    const list = props.billingLockNotificationList ?? [];

    return {
      COMPLETED: list.filter((item) => item.data.performed),
      PENDING: list.filter((item) => !item.data.performed),
    };
  }, [props.billingLockNotificationList]);

  const isDoctor = useMemo(() => {
    const roleTypes: string[] = props.django?.user?.role_types ?? [];

    return roleTypes.includes("DOCTOR");
  }, [props.django]);

  const notiConfig = useMemo(() => {
    const rejectOrderLength = (props.rejectOrderNotificationList ?? []).length;
    const hasBillingLock = (props.billingLockNotificationList ?? []).length > 0;
    const hasRejectOrder = rejectOrderLength > 0;
    const shouldShowNotification = isDoctor ? hasRejectOrder : hasBillingLock;

    let badgeCount: number | string = "";

    if (isDoctor && hasRejectOrder) {
      badgeCount = rejectOrderLength;
    } else if (groupData.PENDING.length > 0) {
      badgeCount = groupData.PENDING.length;
    }

    return {
      count: badgeCount,
      shouldOpen: (!!props.showIconNoti || shouldShowNotification) && isOpen,
      shouldShowBadge: !!props.showIconNoti || groupData.PENDING.length > 0 || hasRejectOrder,
    };
  }, [
    groupData,
    isDoctor,
    isOpen,
    props.billingLockNotificationList,
    props.rejectOrderNotificationList,
    props.showIconNoti,
  ]);

  const trigger = useMemo(
    () => <Icon style={styles.icon} name="bell" onClick={props.onClickBell} />,
    []
  );

  const billingLockContent = useMemo(
    () => (
      <div>
        <strong>
          <label style={{ fontSize: "1.2rem" }}>การแจ้งเตือน</label>
        </strong>
        <div style={{ display: "flex", margin: "0.75rem 0 1rem" }}>
          <Button
            style={tab === "PENDING" ? styles.buttonActive : styles.button}
            name="PENDING"
            size="small"
            onClick={handleChangeTab}
          >
            {TITLES.PENDING}
          </Button>
          <Button
            style={tab === "COMPLETED" ? styles.buttonActive : styles.button}
            name="COMPLETED"
            size="small"
            onClick={handleChangeTab}
          >
            {TITLES.COMPLETED}
          </Button>
        </div>
        <div style={{ height: "75vh", overflowY: "auto" }}>
          {groupData[tab].length === 0 && (
            <div style={{ fontSize: "1.05rem", marginTop: "3rem", textAlign: "center" }}>
              ไม่มีรายการที่{" "}
              <strong>
                <span>{`"${TITLES[tab]}"`}</span>
              </strong>
            </div>
          )}
          {groupData[tab].map((item) => (
            <SubBillLockNotiMessage
              {...item.data}
              key={item.time}
              style={styles.box}
              disabled={item.data.performed}
              onOpenPendingInvoice={handleOpenPendingInvoice(item.data)}
            />
          ))}
        </div>
      </div>
    ),
    [groupData, handleOpenPendingInvoice, tab]
  );

  const rejectOrderContent = useMemo(
    () => (
      <div>
        <strong>
          <label style={{ fontSize: "1.2rem" }}>การแจ้งเตือน</label>
        </strong>
        <div style={{ height: "75vh", overflowY: "auto" }}>
          {(props.rejectOrderNotificationList ?? []).length === 0 && (
            <div style={{ fontSize: "1.05rem", marginTop: "3rem", textAlign: "center" }}>
              ไม่มีรายการ
            </div>
          )}
          <div style={{ marginTop: "1rem" }} />
          {(props.rejectOrderNotificationList ?? []).map((item) => {
            const detail: string = item.reject_detail || "";
            const formattedNames = detail.split("<br/>").map((value) => {
              const [name, ...notes] = value.split(" : ");

              const cleanedName = name.replaceAll(/^\[.*\] /g, "");

              return { name: cleanedName, note: notes.join(" : ") };
            });

            return (
              <div key={`noti-${item.id}`} style={styles.box}>
                <div style={{ lineHeight: 1.75, padding: "0.75rem 4rem 0.75rem 1.5rem" }}>
                  <div style={{ color: "rgba(9, 94, 157, 1)", fontWeight: "bold" }}>
                    คำสั่งรอการแก้ไข
                  </div>
                  <div style={{ marginLeft: "0.75rem" }}>
                    [{item.hn}] {item.patient_name}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "70% 1fr",
                      marginLeft: "0.35rem",
                    }}
                  >
                    {formattedNames.map((acc, index) => (
                      <React.Fragment key={`name-${index}`}>
                        <label style={{ paddingLeft: "0.5rem", paddingRight: "3rem" }}>
                          {acc.name}
                        </label>
                        <label>เหตุผล : {acc.note}</label>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ),
    [props.rejectOrderNotificationList]
  );

  return (
    <div style={{ marginRight: "22px", position: "relative" }}>
      <Popup
        style={styles.popup}
        content={isDoctor ? rejectOrderContent : billingLockContent}
        offset={POPUP_OFFSET}
        open={notiConfig.shouldOpen}
        position="bottom right"
        trigger={trigger}
        on="click"
        onClose={handleClose}
        onOpen={handleOpen}
      />
      {notiConfig.shouldShowBadge && (
        <div
          style={{
            borderRadius: "50%",
            color: "red",
            fontSize: "0.75rem",
            fontWeight: "bold",
            height: "8px",
            position: "absolute",
            right: "6px",
            textAlign: "center",
            top: 0,
            width: "8px",
            ...(typeof notiConfig.count === "string" && { backgroundColor: "red", top: "2px" }),
          }}
        >
          {notiConfig.count}
        </div>
      )}
    </div>
  );
};

NotificationMessage.displayName = "NotificationMessage";

export default React.memo(NotificationMessage);
